import { useMemo } from 'react'
import * as React from 'react'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer'
import TrackedCardCarousel from 'components/Common/TrackedCardCarousel/TrackedCardCarousel'
import CruiseDestinationsSectionItem from './CruiseDestinationsSectionItem'
import TrackingContext from 'contexts/trackingContext'
import qs from 'qs'
import Heading from 'LuxKit/Typography/Heading'
import useCruiseInternationalMarket from 'hooks/useCruiseInternationalMarket'

const italicFormatter = (chunks: React.ReactNode) => <i>{chunks}</i>

const departureCityMessages = defineMessages({
  sydney: {
    id: 'CA-Yaslfw6IClD1ys9kw',
    defaultMessage: 'Sydney',
    description: 'Departure city label for the cruises page departures carousel',
  },
  melbourne: {
    id: 'qDpsTB2m7yOk5wdbBmkf',
    defaultMessage: 'Melbourne',
    description: 'Departure city label for the cruises page departures carousel',
  },
  brisbane: {
    id: 'owL6p9Cq5RUX46IGwn0P',
    defaultMessage: 'Brisbane',
    description: 'Departure city label for the cruises page departures carousel',
  },
  rome: {
    id: 't9BldVdrKAZbaK54DgFC',
    defaultMessage: 'Rome',
    description: 'Departure city label for the cruises page departures carousel',
  },
  tokyo: {
    id: 'fwNyyMkmPZjCnIa1czzb',
    defaultMessage: 'Tokyo',
    description: 'Departure city label for the cruises page departures carousel',
  },
  singapore: {
    id: '2ri4HxOmHuqrFPAN8p6j',
    defaultMessage: 'Singapore',
    description: 'Departure city label for the cruises page departures carousel',
  },
  mediterranean: {
    id: 'vk-uxCGzJs4B_-xIVBXV',
    defaultMessage: 'Mediterranean',
    description: 'Departure region label for the Mediterranean on the cruises page departures carousel',
  },
  northAmerica: {
    id: 'JVkjfLjYrxLpZIaxLlzp',
    defaultMessage: 'North America',
    description: 'Departure region label for North America on the cruises page departures carousel',
  },
  northernEurope: {
    id: 'rGycePIcxnqrlec0YlHr',
    defaultMessage: 'Northern Europe',
    description: 'Departure region label for Northern Europe on the cruises page departures carousel',
  },
  latinAmerica: {
    id: 'ajpYlC34HFKUkumJ99vD',
    defaultMessage: 'Latin America',
    description: 'Departure region label for Latin America on the cruises page departures carousel',
  },
  caribbean: {
    id: '-q6yefA0I6yYyYkGNQeO',
    defaultMessage: 'Caribbean',
    description: 'Departure region label for the Caribbean on the cruises page departures carousel',
  },
  ukGermanyFrance: {
    id: 'CClM4OAKKmi3tEWHadCQ',
    defaultMessage: 'UK, Germany & France',
    description: 'Departure region label for the United Kingdom, Germany and France on the cruises page departures carousel',
  },
})

type DepartureCityId = keyof typeof departureCityMessages

interface DepartureData {
  cityId: DepartureCityId
  image: { id: string }
  filter: string
}

const DEPARTURE_DATA: Array<DepartureData> = [
  {
    cityId: 'sydney',
    image: { id: 'v4kwli3fa6d30n2w1rk9' },
    filter: qs.stringify({
      departureIds: 'le_acf06cdd9c744f969958e1f085554c8b',
      departureNames: 'Sydney',
    }),
  },
  {
    cityId: 'melbourne',
    image: { id: '3wgitcdr0mdfwtfa02ve' },
    filter: qs.stringify({
      departureIds: 'le_bbaa9d6a1445eac881750bea6053f564',
      departureNames: 'Melbourne',
    }),
  },
  {
    cityId: 'brisbane',
    image: { id: 'e3shvrl7vnuw84mb3fbj' },
    filter: qs.stringify({
      departureIds: 'le_67e103b0761e60683e83c559be18d40c',
      departureNames: 'Brisbane',
    }),
  },
  {
    cityId: 'rome',
    image: { id: 'gyk1bjjmb974urlm3ay8' },
    filter: qs.stringify({
      departureIds: 'le_09d37c08f7b129e96277388757530c72,le_675c2bf8873ef7dc8e8b6ba66366a158',
      departureNames: 'Rome',
    }),
  },
  {
    cityId: 'tokyo',
    image: { id: 'hrl9agi1rgthavmoqtdp' },
    filter: qs.stringify({
      departureIds: 'le_cdd0500dc0ef6682fa6ec6d2e6b577c4',
      departureNames: 'Tokyo',
    }),
  },
  {
    cityId: 'singapore',
    image: { id: 'e83ps9azewdouwruvet' },
    filter: qs.stringify({
      departureIds: 'le_ce393994e8cf430867408678d1a5a9c8',
      departureNames: 'Singapore',
    }),
  },
]

const INTERNATIONAL_DEPARTURE_DATA: Array<DepartureData> = [
  {
    cityId: 'northernEurope',
    image: { id: 'edext8oubg3vyfjdj8d' },
    filter: qs.stringify({
      departureIds: 'le_93d9ce9e859971d13fa6475ee1f6cb57',
      departureNames: 'Northern Europe',
    }),
  },
  {
    cityId: 'northAmerica',
    image: { id: 'p28suam4m7fnt7csh7' },
    filter: qs.stringify({
      departureIds: 'le_cf874aad79e14b401a4c86954a596fa5',
      departureNames: 'North America',
    }),
  },
  {
    cityId: 'mediterranean',
    image: { id: 'deh51ookkm0egm5dy8w6' },
    filter: qs.stringify({
      departureIds: 'le_93db85ed909c13838ff95ccfa94cebd9',
      departureNames: 'Mediterranean',
    }),
  },
  {
    cityId: 'latinAmerica',
    image: { id: 'ad8j4yxoffd97g9jhj0q' },
    filter: qs.stringify({
      departureIds: 'le_ded8e3f6fa8da10375ce4f89e1124893',
      departureNames: 'Latin America',
    }),
  },
  {
    cityId: 'caribbean',
    image: { id: 'bihvmclzh6v5r5shea7' },
    filter: qs.stringify({
      departureIds: 'le_d7bce4d8dbac13945471c343c4973ba3',
      departureNames: 'Caribbean',
    }),
  },
  {
    cityId: 'ukGermanyFrance',
    image: { id: 'nnijsnnfnb21a1kaez1' },
    filter: qs.stringify({
      departureIds: 'le_cfecdb276f634854f3ef915e2e980c31,le_ea5d2f1c4608232e07d3aa3d998e5135,le_093f65e080a295f8076b1c5722a46aa2',
      departureNames: 'UK, Germany & France',
    }),
  },
]

function CruisePageDeparturesSection() {
  const intl = useIntl()
  const isInternationalMarket = useCruiseInternationalMarket()
  const departureData = isInternationalMarket ? INTERNATIONAL_DEPARTURE_DATA : DEPARTURE_DATA

  const departures = useMemo(() => departureData.map((departure) => ({
    ...departure,
    name: intl.formatMessage(
      {
        id: 'SHoh_R-xceawVPI8MzxD',
        defaultMessage: 'Cruises from {city}',
        description: 'Tile heading for cruises departing from a specific city',
      },
      { city: intl.formatMessage(departureCityMessages[departure.cityId]) },
    ),
  })), [departureData, intl])

  const viewOffersLabel = intl.formatMessage({
    id: 'pzKfqt33bAHKG9r85Qts',
    defaultMessage: 'View offers',
    description: 'Call to action button text to view cruise offers from a departure port',
  })

  return (
    <TrackingContext.Provider
      value={{
        category: 'cruise_departures',
        label: 'Cruise Page Departures',
        listId: 'cruise-departures-carousel',
        listName: 'Cruise Page Departures',
      }}
    >
      <Group gap={32} direction="vertical">
        <LayoutContainer>
          <Heading variant="heading3">
            <FormattedMessage
              id="PUtT163WKS7TFTaTIOAI"
              defaultMessage="Top ports to start your <i>adventure</i>"
              description="Section heading for top cruise departure ports in the cruises section"
              values={{ i: italicFormatter }}
            />
          </Heading>
        </LayoutContainer>

        <LayoutContainer>
          <TrackedCardCarousel pageSize={6} width={rem(180)}>
            {departures.map((departure, index) => (
              <CruiseDestinationsSectionItem
                key={departure.cityId}
                destination={departure}
                position={index}
                href={`/search/cruises?${departure.filter}`}
                image={departure.image}
                heading={departure.name}
                subHeading=""
                align="top"
                callToAction={viewOffersLabel}
              />
            ))}
          </TrackedCardCarousel>
        </LayoutContainer>
      </Group>
    </TrackingContext.Provider>
  )
}

export default CruisePageDeparturesSection
